import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import axios from 'axios';
import { LogOut, Plus, Pencil, Trash2, ExternalLink, Loader2, Upload } from 'lucide-react';

const API = typeof window !== 'undefined'
  ? `${process.env.REACT_APP_API_URL || window.location.origin}/api`
  : '/api';

function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!supabase) {
      setError('Supabase er ikke konfigurert. Legg til REACT_APP_SUPABASE_URL og REACT_APP_SUPABASE_ANON_KEY.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
      if (err) throw err;
      onLogin(data.session);
    } catch (err) {
      setError(err.message || 'Innlogging feilet. Sjekk e-post og passord.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted p-6">
      <div className="w-full max-w-md bg-card border border-border p-8 shadow-lg">
        <h1 className="font-syne font-bold text-2xl text-primary mb-2">Admin-innlogging</h1>
        <p className="font-manrope text-sm text-muted-foreground mb-6">
          Logg inn for å administrere prosjekter
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/30 rounded text-destructive text-sm">
              {error}
            </div>
          )}
          <div>
            <label className="block font-manrope text-sm font-medium text-primary mb-2">E-post</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-border rounded focus:outline-none focus:ring-2 focus:ring-accent font-manrope"
              placeholder="admin@sidekraft.no"
              required
            />
          </div>
          <div>
            <label className="block font-manrope text-sm font-medium text-primary mb-2">Passord</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-border rounded focus:outline-none focus:ring-2 focus:ring-accent font-manrope"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-primary text-primary-foreground font-syne font-bold uppercase tracking-wide hover:bg-accent transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Logg inn'}
          </button>
        </form>
      </div>
    </div>
  );
}

function AdminDashboard({ session, onLogout }) {
  const [prosjekter, setProsjekter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', url: '', domain: '', category: 'Annet', description: '', services: [], image: '' });
  const [imageUploading, setImageUploading] = useState(false);

  const fetchProjects = () => {
    axios.get(`${API}/projects`).then((res) => {
      setProsjekter(Array.isArray(res.data) ? res.data : []);
    }).catch(() => setProsjekter([])).finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleSeed = () => {
    setLoading(true);
    axios.post(`${API}/projects`, { action: 'seed' })
      .then(() => fetchProjects())
      .catch((err) => alert(err.response?.data?.error || 'Feil ved import'))
      .finally(() => setLoading(false));
  };

  const handleCreate = (e) => {
    e.preventDefault();
    if (!form.name?.trim() || !form.url?.trim() || !form.description?.trim()) {
      alert('Navn, URL og beskrivelse er påkrevd.');
      return;
    }
    if (imageUploading) {
      alert('Vent til bildeopplasting er ferdig.');
      return;
    }
    setLoading(true);
    axios.post(`${API}/projects`, {
      ...form,
      name: form.name.trim(),
      url: form.url.trim(),
      description: form.description.trim(),
      services: Array.isArray(form.services) ? form.services : (form.services || '').split(',').map((s) => s.trim()).filter(Boolean),
    })
      .then(() => {
        setForm({ name: '', url: '', domain: '', category: 'Annet', description: '', services: [], image: '' });
        fetchProjects();
      })
      .catch((err) => {
        const msg = err.response?.data?.error || err.message || 'Feil ved opprettelse';
        alert(msg);
      })
      .finally(() => setLoading(false));
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    if (!editing || !form.name || !form.url || !form.description) return;
    setLoading(true);
    axios.put(`${API}/projects`, {
      id: editing.id,
      ...form,
      services: Array.isArray(form.services) ? form.services : (form.services || '').split(',').map((s) => s.trim()).filter(Boolean),
    })
      .then(() => {
        setEditing(null);
        setForm({ name: '', url: '', domain: '', category: 'Annet', description: '', services: [], image: '' });
        fetchProjects();
      })
      .catch((err) => alert(err.response?.data?.error || 'Feil ved oppdatering'))
      .finally(() => setLoading(false));
  };

  const handleDelete = (id) => {
    if (!window.confirm('Er du sikker på at du vil slette dette prosjektet?')) return;
    setLoading(true);
    axios.delete(`${API}/projects`, { data: { id } })
      .then(() => fetchProjects())
      .catch((err) => alert(err.response?.data?.error || 'Feil ved sletting'))
      .finally(() => setLoading(false));
  };

  const startEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      url: p.url,
      domain: p.domain || '',
      category: p.category || 'Annet',
      description: p.description,
      services: Array.isArray(p.services) ? p.services : [],
      image: p.image || '',
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !supabase) return;
    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const allowed = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
    if (!allowed.includes(ext)) {
      alert('Kun JPG, PNG, GIF og WebP er tillatt.');
      return;
    }
    setImageUploading(true);
    try {
      const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { data, error } = await supabase.storage.from('project-images').upload(path, file, { upsert: true });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('project-images').getPublicUrl(data.path);
      setForm((f) => ({ ...f, image: publicUrl }));
    } catch (err) {
      const msg = err?.message || err?.error || JSON.stringify(err?.details || err) || 'Kunne ikke laste opp bilde. Sjekk at storage-bucket "project-images" er opprettet i Supabase (kjør supabase/projects.sql).';
      alert(msg);
    } finally {
      setImageUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-muted p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-syne font-bold text-2xl text-primary">Admin – Prosjekter</h1>
          <button
            onClick={onLogout}
            className="flex items-center gap-2 px-4 py-2 text-sm font-manrope text-muted-foreground hover:text-primary"
          >
            <LogOut className="w-4 h-4" />
            Logg ut
          </button>
        </div>

        <div className="bg-card border border-border p-6 mb-6">
          <h2 className="font-syne font-bold text-lg mb-4">{editing ? 'Rediger prosjekt' : 'Nytt prosjekt'}</h2>
          <form onSubmit={editing ? handleUpdate : handleCreate} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                placeholder="Navn"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="px-3 py-2 border border-border rounded font-manrope text-sm"
                required
              />
              <input
                placeholder="URL"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                className="px-3 py-2 border border-border rounded font-manrope text-sm"
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                placeholder="Domene (valgfritt)"
                value={form.domain}
                onChange={(e) => setForm({ ...form, domain: e.target.value })}
                className="px-3 py-2 border border-border rounded font-manrope text-sm"
              />
              <input
                placeholder="Kategori"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="px-3 py-2 border border-border rounded font-manrope text-sm"
              />
            </div>
            <textarea
              placeholder="Beskrivelse"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded font-manrope text-sm"
              rows={2}
              required
            />
            <div className="space-y-2">
              <label className="block font-manrope text-sm font-medium text-muted-foreground">Bilde (valgfritt)</label>
              <input
                type="url"
                placeholder="Bilde-URL"
                value={form.image}
                onChange={(e) => setForm({ ...form, image: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded font-manrope text-sm"
              />
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="project-image-upload"
                  disabled={imageUploading}
                />
                <label
                  htmlFor="project-image-upload"
                  className={`inline-flex items-center gap-2 px-4 py-2 border border-border rounded font-manrope text-sm cursor-pointer hover:bg-muted transition-colors ${imageUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {imageUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {imageUploading ? 'Laster opp...' : 'Last opp bilde'}
                </label>
                {form.image && (
                  <span className="text-xs text-muted-foreground truncate max-w-[200px]">✓ Bilde valgt</span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={loading || imageUploading}
                className="px-4 py-2 bg-primary text-primary-foreground font-manrope text-sm hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editing ? 'Lagre' : 'Opprett'}
              </button>
              {editing && (
                <button
                  type="button"
                  onClick={() => { setEditing(null); setForm({ name: '', url: '', domain: '', category: 'Annet', description: '', services: [], image: '' }); }}
                  className="px-4 py-2 border border-border font-manrope text-sm"
                >
                  Avbryt
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="flex gap-2 mb-4">
          <button
            onClick={handleSeed}
            disabled={loading || prosjekter.length > 0}
            className="px-4 py-2 bg-accent text-accent-foreground font-manrope text-sm hover:opacity-90 disabled:opacity-50"
          >
            Importer standard prosjekter
          </button>
        </div>

        {loading && prosjekter.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-2">
            {prosjekter.map((p) => (
              <div
                key={p.id}
                className="bg-card border border-border p-4 flex items-center justify-between"
              >
                <div>
                  <span className="font-syne font-bold text-primary">{p.name}</span>
                  <span className="text-muted-foreground text-sm ml-2">({p.category})</span>
                </div>
                <div className="flex items-center gap-2">
                  <a href={p.url} target="_blank" rel="noopener noreferrer" className="p-2 hover:text-accent">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button onClick={() => startEdit(p)} className="p-2 hover:text-accent">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(p.id)} className="p-2 hover:text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!supabase) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted p-6">
        <div className="max-w-md bg-card border border-border p-8 text-center">
          <h1 className="font-syne font-bold text-xl text-primary mb-4">Admin krever Supabase</h1>
          <p className="font-manrope text-muted-foreground text-sm mb-4">
            Legg til REACT_APP_SUPABASE_URL og REACT_APP_SUPABASE_ANON_KEY i Vercel Environment Variables. Se ADMIN-SETUP.md for instruksjoner.
          </p>
        </div>
      </div>
    );
  }

  if (!session) {
    return <AdminLogin onLogin={() => {}} />;
  }

  return (
    <AdminDashboard
      session={session}
      onLogout={() => supabase?.auth.signOut()}
    />
  );
}
